'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { SearchIcon, FilterIcon, XIcon, CalendarIcon, ImageIcon, HardDriveIcon, LockIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'
import { FilesFilterParams } from '@/types/file'
import { cn } from '@/lib/utils'
import { AccessLevelEnum, FileStorageDriverEnum } from '@/enums'

type PropsType = {
  filters: FilesFilterParams
  onFiltersChange: (filters: Partial<FilesFilterParams>) => void
  lockedType?: boolean
  onResetFilters: () => void
}

const Filters = ({ filters, onFiltersChange, lockedType = false, onResetFilters }: PropsType) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const { register, handleSubmit, watch, setValue, reset } = useForm<FilesFilterParams>({
    defaultValues: filters
  })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchAll = watch()

  const onSubmit = (data: FilesFilterParams) => {
    onFiltersChange({ ...data, page: 1 })
    setIsOpen(false)
  }

  const handleClearSearch = () => {
    setValue('q', '')
  }

  const formatDateLabel = (value?: string) => (value ? format(new Date(value), 'dd.MM.yyyy') : t('start_date'))

  const handleDriverChange = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setValue('drivers', value.length > 0 ? (value as FileStorageDriverEnum[]) : undefined)
    } else {
      setValue('drivers', undefined)
    }
  }

  const handleAccessLevelChange = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setValue('accessLevels', value.length > 0 ? (value as AccessLevelEnum[]) : undefined)
    } else {
      setValue('accessLevels', undefined)
    }
  }

  const handleOnlyImageChange = (checked: boolean) => {
    // Only block changes when lockedType is true
    if (lockedType) {
      return
    }
    setValue('onlyImages', checked)
  }

  const handleCalendarChange = (field: keyof FilesFilterParams, date?: Date) => {
    if (date) {
      setValue(field, date.toISOString())
    } else {
      setValue(field, undefined)
    }
  }

  const hasActiveFilters = () => {
    return (
      watchAll.q ||
      watchAll.drivers ||
      watchAll.accessLevels ||
      watchAll.onlyImages ||
      watchAll.createdAtStart ||
      watchAll.createdAtEnd ||
      watchAll.updatedAtStart ||
      watchAll.updatedAtEnd
    )
  }

  return (
    <div className="flex items-center justify-end">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button type="button" variant="outline">
            <FilterIcon className="h-4 w-4" />
            <span className="hidden flex-row items-center justify-items-center lg:flex">{t('filters')}</span>
            {hasActiveFilters() && (
              <span className="bg-primary text-primary-foreground flex h-5 w-5 items-center justify-center rounded-full text-xs">
                !
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('filter_records')}</SheetTitle>
            <SheetDescription>{t('filter_description')}</SheetDescription>
          </SheetHeader>

          <ScrollArea>
            <div className="space-y-6 p-4">
              <div className="space-y-2">
                <Label htmlFor="filter-q">{t('keyword')}</Label>
                <div className="relative">
                  <Input
                    placeholder={t('keyword')}
                    id="filter-q"
                    {...register('q')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    icon={<SearchIcon className="h-4 w-4" />}
                    className="pr-8"
                  />
                  {watchAll.q && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1.5 right-1 h-6 w-6 p-0"
                      onClick={handleClearSearch}
                      type="button">
                      <XIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('driver')}</Label>
                  <Combobox
                    options={Object.values(FileStorageDriverEnum).map(driver => ({
                      label: t(driver),
                      value: driver,
                      searchValue: t(driver)
                    }))}
                    value={watchAll.drivers || []}
                    onValueChange={handleDriverChange}
                    placeholder={
                      watchAll.drivers && watchAll.drivers.length > 0
                        ? `${watchAll.drivers.length} ${t('selected')}`
                        : t('select')
                    }
                    searchPlaceholder={t('search')}
                    emptyText={t('no_results')}
                    multiple={true}
                    clearable={true}
                    icon={<HardDriveIcon className="h-4 w-4" />}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('access_level')}</Label>
                  <Combobox
                    options={Object.values(AccessLevelEnum).map(level => ({
                      label: t(level),
                      value: level,
                      searchValue: t(level)
                    }))}
                    value={watchAll.accessLevels || []}
                    onValueChange={handleAccessLevelChange}
                    placeholder={
                      watchAll.accessLevels && watchAll.accessLevels.length > 0
                        ? `${watchAll.accessLevels.length} ${t('selected')}`
                        : t('select')
                    }
                    searchPlaceholder={t('search')}
                    emptyText={t('no_results')}
                    multiple={true}
                    clearable={true}
                    icon={<LockIcon className="h-4 w-4" />}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="onlyImage"
                  checked={!!watchAll.onlyImages}
                  onCheckedChange={handleOnlyImageChange}
                  disabled={lockedType}
                />
                <Label
                  htmlFor="onlyImage"
                  className={`${lockedType ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                  <div className="flex items-center">
                    <ImageIcon className="mr-2 h-4 w-4" />
                    {t('only_images')}
                    {lockedType && <span className="text-muted-foreground ml-2 text-xs">({t('locked')})</span>}
                  </div>
                </Label>
              </div>

              {/** CREATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('created_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['createdAtStart', 'createdAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'createdAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              {/** UPDATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('updated_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['updatedAtStart', 'updatedAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'updatedAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              <div className="flex flex-row gap-2">
                <Button type="button" onClick={() => handleSubmit(onSubmit)()} variant="default" className="flex-1">
                  <FilterIcon className="h-4 w-4" />
                  {t('filter')}
                </Button>
                <Button
                  onClick={() => {
                    reset()
                    onResetFilters()
                  }}
                  variant="destructive"
                  className="flex-1"
                  type="button">
                  <XIcon className="h-4 w-4" />
                  {t('clear_filters')}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default Filters
